import React from 'react'
import {
    LayoutDashboard,
    Users,
    Building2,
    UserCog,
    Clock,
    FileText,
    Settings,
    Grid3x3,
    FolderTree,
    Database,
    ChevronDown,
    ChevronUp,
    Menu as MenuIcon,
    Shield,
    Mail,
    Lock,
    Sun,
    Moon,
    AlertCircle,
} from 'lucide-react'

interface IconDemoProps {
    icon: React.ReactNode
    name: string
    description: string
    oldIcon?: string
}

const IconDemo: React.FC<IconDemoProps> = ({ icon, name, description, oldIcon }) => (
    <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-lg transition-all hover:scale-105">
        <div className="flex flex-col items-center gap-4">
            {/* Ícone Grande */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400">
                {icon}
            </div>

            {/* Nome */}
            <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{name}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{description}</p>
                {oldIcon && (
                    <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                        Antes: <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">{oldIcon}</code>
                    </p>
                )}
            </div>
        </div>
    </div>
)

const IconShowcasePage: React.FC = () => {
    const menuIcons = [
        {
            icon: <LayoutDashboard size={48} />,
            name: 'Dashboard',
            description: 'Visão geral do sistema',
            oldIcon: 'DashboardIcon',
        },
        {
            icon: <Users size={48} />,
            name: 'Grupos',
            description: 'Grupos de usuários',
            oldIcon: 'GroupIcon',
        },
        {
            icon: <Building2 size={48} />,
            name: 'Empresas',
            description: 'Gerenciar empresas',
            oldIcon: 'BusinessIcon',
        },
        {
            icon: <FolderTree size={48} />,
            name: 'Áreas Funcionais',
            description: 'Organização hierárquica',
            oldIcon: 'CategoryIcon',
        },
        {
            icon: <UserCog size={48} />,
            name: 'Manutenção de Usuários',
            description: 'Gerenciar usuários',
            oldIcon: 'BuildIcon',
        },
        {
            icon: <Shield size={48} />,
            name: 'Audit Log',
            description: 'Segurança e auditoria',
            oldIcon: 'ListAltIcon',
        },
        {
            icon: <Database size={48} />,
            name: 'Backups',
            description: 'Backup de dados',
            oldIcon: 'BackupIcon',
        },
        {
            icon: <Grid3x3 size={48} />,
            name: 'Gerenciar Telas',
            description: 'Layout de telas',
            oldIcon: 'AppsIcon',
        },
        {
            icon: <MenuIcon size={48} />,
            name: 'Menu ThorJava',
            description: 'Menu do sistema',
            oldIcon: 'AppsIcon',
        },
        {
            icon: <Clock size={48} />,
            name: 'Sessões',
            description: 'Sessões ativas',
            oldIcon: 'HistoryIcon',
        },
        {
            icon: <FileText size={48} />,
            name: 'Logs',
            description: 'Arquivos de log',
            oldIcon: 'ListAltIcon',
        },
        {
            icon: <Settings size={48} />,
            name: 'Configurações',
            description: 'Configurações do sistema',
            oldIcon: 'SettingsIcon',
        },
    ]

    const uiIcons = [
        {
            icon: <Mail size={48} />,
            name: 'Email',
            description: 'Campo de email',
            oldIcon: '-',
        },
        {
            icon: <Lock size={48} />,
            name: 'Senha',
            description: 'Campo de senha',
            oldIcon: '-',
        },
        {
            icon: <Sun size={48} />,
            name: 'Modo Claro',
            description: 'Ativar tema claro',
            oldIcon: '-',
        },
        {
            icon: <Moon size={48} />,
            name: 'Modo Escuro',
            description: 'Ativar tema escuro',
            oldIcon: '-',
        },
        {
            icon: <AlertCircle size={48} />,
            name: 'Alerta',
            description: 'Mensagens de erro',
            oldIcon: '-',
        },
        {
            icon: <ChevronDown size={48} />,
            name: 'Expandir',
            description: 'Expandir submenu',
            oldIcon: 'ExpandMore',
        },
        {
            icon: <ChevronUp size={48} />,
            name: 'Recolher',
            description: 'Recolher submenu',
            oldIcon: 'ExpandLess',
        },
    ]

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-8">
            <div className="w-full space-y-12">
                {/* Header */}
                <div className="text-center space-y-4">
                    <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
                        🎨 Showcase de Ícones Modernizados
                    </h1>
                    <p className="text-xl text-slate-600 dark:text-slate-400">
                        Lucide React - Ícones mais modernos, intuitivos e leves
                    </p>
                    <div className="flex items-center justify-center gap-4 text-sm text-slate-500 dark:text-slate-500">
                        <span className="flex items-center gap-2">
                            <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                            Material UI → Lucide React
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                            -60% Bundle Size
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                            Tamanho Consistente (20px)
                        </span>
                    </div>
                </div>

                {/* Ícones do Menu Lateral */}
                <section>
                    <div className="mb-6">
                        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                            Ícones do Menu Lateral
                        </h2>
                        <p className="text-slate-600 dark:text-slate-400">
                            Ícones usados no Sidebar para navegação principal
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {menuIcons.map((item, index) => (
                            <IconDemo key={index} {...item} />
                        ))}
                    </div>
                </section>

                {/* Ícones de UI */}
                <section>
                    <div className="mb-6">
                        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                            Ícones de Interface
                        </h2>
                        <p className="text-slate-600 dark:text-slate-400">
                            Ícones usados em formulários, botões e componentes
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {uiIcons.map((item, index) => (
                            <IconDemo key={index} {...item} />
                        ))}
                    </div>
                </section>

                {/* Comparação de Tamanhos */}
                <section className="bg-white dark:bg-slate-800 p-8 rounded-2xl border border-slate-200 dark:border-slate-700">
                    <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">
                        Tamanhos Disponíveis
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center space-y-4">
                            <div className="flex justify-center">
                                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400">
                                    <LayoutDashboard size={16} />
                                </div>
                            </div>
                            <div>
                                <p className="font-semibold text-slate-900 dark:text-white">Pequeno</p>
                                <p className="text-sm text-slate-600 dark:text-slate-400">16px - Botões compactos</p>
                            </div>
                        </div>
                        <div className="text-center space-y-4">
                            <div className="flex justify-center">
                                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400">
                                    <LayoutDashboard size={20} />
                                </div>
                            </div>
                            <div>
                                <p className="font-semibold text-slate-900 dark:text-white">Médio (Padrão)</p>
                                <p className="text-sm text-slate-600 dark:text-slate-400">20px - Menu lateral</p>
                            </div>
                        </div>
                        <div className="text-center space-y-4">
                            <div className="flex justify-center">
                                <div className="p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400">
                                    <LayoutDashboard size={24} />
                                </div>
                            </div>
                            <div>
                                <p className="font-semibold text-slate-900 dark:text-white">Grande</p>
                                <p className="text-sm text-slate-600 dark:text-slate-400">24px - Destaque</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Estatísticas */}
                <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl text-white">
                        <div className="text-4xl font-bold mb-2">19</div>
                        <div className="text-blue-100">Ícones Atualizados</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl text-white">
                        <div className="text-4xl font-bold mb-2">-60%</div>
                        <div className="text-green-100">Redução de Bundle</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl text-white">
                        <div className="text-4xl font-bold mb-2">100%</div>
                        <div className="text-purple-100">Acessibilidade</div>
                    </div>
                </section>

                {/* Footer */}
                <div className="text-center text-slate-500 dark:text-slate-500 text-sm">
                    <p>Ícones fornecidos por <a href="https://lucide.dev" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Lucide Icons</a></p>
                    <p className="mt-2">Implementado em 07/02/2026 - Galatea Platform</p>
                </div>
            </div>
        </div>
    )
}

export default IconShowcasePage
